import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Estado global del modal de canje de Perrón Points, análogo a
 * `StockAlertModalService` pero sin contexto de producto: el modal se
 * autoconecta a este servicio (montado una única vez en `app.ts`) y
 * cualquier surface (header, checkout, etc.) sólo llama `open()`.
 *
 * Bloquea el scroll del body mientras está abierto y cierra automáticamente
 * en cada `NavigationEnd`.
 */
@Injectable({ providedIn: 'root' })
export class PerronPointsModalService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router = inject(Router);

  private readonly _isOpen = signal(false);
  readonly isOpen = computed(() => this._isOpen());

  /** `true` mientras un canje está en vuelo (lo marca el modal). Bloquea `close()`. */
  private readonly _redeeming = signal(false);

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;
      document.body.style.overflow = this._isOpen() ? 'hidden' : '';
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.close());
  }

  open(): void {
    this._isOpen.set(true);
  }

  /**
   * No-op mientras hay un canje en vuelo (ver `setRedeeming`): evita que un
   * `NavigationEnd` (o cualquier otro caller) cierre el modal a mitad de la
   * petición y pierda el resultado del canje.
   */
  close(): void {
    if (this._redeeming()) return;
    this._isOpen.set(false);
  }

  setRedeeming(value: boolean): void {
    this._redeeming.set(value);
  }
}
